/**
 * Surveillance data access for CM-UI.
 *
 * Merged `/surveillance` page picks mode via Bet Ticker Previous 7-day switch:
 * Live (`mode: 'live'`): Postgres pickers + getBetsForSurveilliance.
 * Archive (`mode: 'archive'`): BQ pickers + bqGetBetsForSurv.
 *
 * Set `VITE_USE_STATIC_SURVEILLANCE=true` (or `__setUseStaticSurveillance(true)`
 * in tests) to force the demo fixtures for all methods.
 */

import { cmApi } from '@/api/cmApi'
import {
  SURVEILLANCE_STATIC_ERRORS,
  assertSurveillanceListOk,
  mapGetBetsForSurveilliance,
  mapSurveillanceEvents,
  mapSurveillanceMarkets,
  mapSurveillanceSubGames,
  surveillanceCatchError,
} from '@/api/map/surveillance'
import { searchHierarchyNew, searchHierarchyNewUsernames } from '@/api/map/member'
import { authHeaders } from '@/auth/session'
import {
  STATIC_EVENTS,
  STATIC_GRAPHS,
  STATIC_MARKETS,
  STATIC_MEMBERS,
  STATIC_SUB_GAMES,
  emptyGraphForMarket,
} from '@/features/surveillance/staticData'
import {
  filterBetsForDisplay,
  validateStakeRange,
} from '@/features/surveillance/surveillanceFilters'
import type { SurveillanceGraphViewType } from '@/features/surveillance/graphViewType'
import type {
  GetGraphResult,
  ListEventsResult,
  ListMarketsResult,
  ListSubGamesResult,
  RegenerateGraphResult,
  SearchMembersResult,
  SurveillanceGraphFilter,
  SurveillanceMode,
} from '@/features/surveillance/types'

/** `true` = demo fixtures; default live CM-API pickers. Tests may toggle. */
export let USE_STATIC_SURVEILLANCE =
  String(import.meta.env.VITE_USE_STATIC_SURVEILLANCE ?? '').toLowerCase() ===
  'true'

export function __setUseStaticSurveillance(value: boolean): void {
  USE_STATIC_SURVEILLANCE = value
}

function delay(ms = 140): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

function dayStart(iso: string): number {
  return new Date(`${iso}T00:00:00.000Z`).getTime()
}

function dayEnd(iso: string): number {
  return new Date(`${iso}T23:59:59.999Z`).getTime()
}

async function staticListSubGames(_mode: SurveillanceMode): Promise<ListSubGamesResult> {
  await delay()
  return { ok: true, subGames: STATIC_SUB_GAMES.map((s) => ({ ...s })) }
}

async function staticListEvents(input: {
  mode: SurveillanceMode
  subGameId: string
  subGameName?: string
  keyword?: string
  fromDate?: string
  toDate?: string
}): Promise<ListEventsResult> {
  await delay()
  let events = STATIC_EVENTS.filter((e) => e.subGameId === input.subGameId)
  if (input.keyword?.trim()) {
    const q = input.keyword.trim().toLowerCase()
    events = events.filter((e) => e.name.toLowerCase().includes(q))
  }
  if (input.mode === 'archive' && (input.fromDate || input.toDate)) {
    const from = input.fromDate ? dayStart(input.fromDate) : Number.NEGATIVE_INFINITY
    const to = input.toDate ? dayEnd(input.toDate) : Number.POSITIVE_INFINITY
    events = events.filter((e) => {
      if (!e.openDate) return true
      const t = new Date(e.openDate).getTime()
      return t >= from && t <= to
    })
  }
  return {
    ok: true,
    events: events
      .slice()
      .sort((a, b) => (b.openDate ?? '').localeCompare(a.openDate ?? '')),
  }
}

async function staticListMarkets(input: {
  mode?: SurveillanceMode
  eventId: string
  fromDate?: string
  toDate?: string
  subGameName?: string
}): Promise<ListMarketsResult> {
  await delay()
  return {
    ok: true,
    markets: STATIC_MARKETS.filter((m) => m.eventId === input.eventId),
  }
}

async function staticSearchMembers(filter: string): Promise<SearchMembersResult> {
  await delay(80)
  const q = filter.trim().toLowerCase()
  const usernames = STATIC_MEMBERS.filter((u) => !q || u.includes(q))
  return { ok: true, usernames: [...usernames] }
}

async function staticGetGraph(filter: SurveillanceGraphFilter): Promise<GetGraphResult> {
  await delay(220)
  const stakeErr = validateStakeRange(filter.fromStake, filter.toStake)
  if (stakeErr) return { ok: false, message: stakeErr }
  if (!filter.eventType || !filter.eventId || !filter.marketId) {
    return { ok: false, message: 'Select sub game, event and market' }
  }

  const base = STATIC_GRAPHS[filter.marketId]
  const market = STATIC_MARKETS.find((m) => m.betfairId === filter.marketId)
  const graph = base
    ? structuredClone(base)
    : market
      ? emptyGraphForMarket(market)
      : null
  if (!graph) return { ok: false, message: 'Unable to find market' }

  graph.h = graph.h.map((runner) => ({
    ...runner,
    b: filterBetsForDisplay(runner.b, filter),
  }))

  return { ok: true, data: graph }
}

async function staticRegenerateGraph(
  marketId: string,
  graphType: SurveillanceGraphViewType = 'NORMAL',
): Promise<RegenerateGraphResult> {
  await delay(180)
  if (!marketId) {
    return { ok: true, message: 'Unable to generate graph' }
  }
  return {
    ok: true,
    message: `Graph generation added in queue (static demo · ${graphType})`,
  }
}

// --- CM-API HTTP -----------------------------------------------------------

export type ListSubGamesOpts = {
  fromDate?: string
  toDate?: string
}

async function httpListSubGames(
  mode: SurveillanceMode,
  opts?: ListSubGamesOpts,
): Promise<ListSubGamesResult> {
  try {
    if (mode === 'archive') {
      const body: { fromDate?: string; toDate?: string } = {}
      if (opts?.fromDate?.trim()) body.fromDate = opts.fromDate.trim()
      if (opts?.toDate?.trim()) body.toDate = opts.toDate.trim()
      const res = await cmApi.bqGetSubGamesForSurv(authHeaders(), body)
      assertSurveillanceListOk(res, SURVEILLANCE_STATIC_ERRORS.subGames)
      return { ok: true, subGames: mapSurveillanceSubGames(res) }
    }
    const res = await cmApi.getSubGamesForSurveilliance(authHeaders(), {})
    assertSurveillanceListOk(res, SURVEILLANCE_STATIC_ERRORS.subGames)
    return { ok: true, subGames: mapSurveillanceSubGames(res) }
  } catch (err) {
    throw surveillanceCatchError(err, SURVEILLANCE_STATIC_ERRORS.subGames)
  }
}

function buildEventsBody(input: {
  subGame: string
  keyword?: string
  fromDate?: string
  toDate?: string
  playerNames?: string[]
  fromStake?: number | null
  toStake?: number | null
  currencyType?: string
}): {
  subGame: string
  name?: string
  fromDate?: string
  toDate?: string
  currencyType?: string
  fromStake?: number
  toStake?: number
  playerName?: string[]
} {
  const body: {
    subGame: string
    name?: string
    fromDate?: string
    toDate?: string
    currencyType?: string
    fromStake?: number
    toStake?: number
    playerName?: string[]
  } = { subGame: input.subGame }
  if (input.keyword?.trim()) body.name = input.keyword.trim()
  if (input.fromDate?.trim()) body.fromDate = input.fromDate.trim()
  if (input.toDate?.trim()) body.toDate = input.toDate.trim()
  if (input.currencyType?.trim()) body.currencyType = input.currencyType.trim()
  if (input.fromStake != null) body.fromStake = input.fromStake
  if (input.toStake != null) body.toStake = input.toStake
  if (input.playerNames?.length) body.playerName = [...input.playerNames]
  return body
}

async function httpListEvents(input: {
  mode: SurveillanceMode
  subGameId: string
  subGameName?: string
  keyword?: string
  fromDate?: string
  toDate?: string
  playerNames?: string[]
  fromStake?: number | null
  toStake?: number | null
  currencyType?: string
}): Promise<ListEventsResult> {
  const subGame = (input.subGameName ?? '').trim()
  if (!subGame) {
    return { ok: true, events: [] }
  }

  try {
    const body = buildEventsBody({ ...input, subGame })

    if (input.mode === 'archive') {
      const res = await cmApi.bqGetEventsForBetTickerSurv(authHeaders(), body)
      assertSurveillanceListOk(res, SURVEILLANCE_STATIC_ERRORS.events)
      const events = mapSurveillanceEvents(res, input.subGameId)
        .slice()
        .sort((a, b) => (b.openDate ?? '').localeCompare(a.openDate ?? ''))
      return { ok: true, events }
    }

    const res = await cmApi.getEventsForBetTickerForSurveilliance(
      authHeaders(),
      body,
    )
    assertSurveillanceListOk(res, SURVEILLANCE_STATIC_ERRORS.events)
    const events = mapSurveillanceEvents(res, input.subGameId)
      .slice()
      .sort((a, b) => (b.openDate ?? '').localeCompare(a.openDate ?? ''))
    return { ok: true, events }
  } catch (err) {
    throw surveillanceCatchError(err, SURVEILLANCE_STATIC_ERRORS.events)
  }
}

async function httpListMarkets(input: {
  mode?: SurveillanceMode
  eventId: string
  fromDate?: string
  toDate?: string
  subGameName?: string
}): Promise<ListMarketsResult> {
  const event = input.eventId.trim()
  if (!event) return { ok: true, markets: [] }

  const body: {
    event: string
    fromDate?: string
    toDate?: string
    subGame?: string
  } = { event }
  if (input.fromDate?.trim()) body.fromDate = input.fromDate.trim()
  if (input.toDate?.trim()) body.toDate = input.toDate.trim()
  if (input.subGameName?.trim()) body.subGame = input.subGameName.trim()

  try {
    if (input.mode === 'archive') {
      const res = await cmApi.bqGetMarketsForSurv(authHeaders(), body)
      assertSurveillanceListOk(res, SURVEILLANCE_STATIC_ERRORS.markets)
      return { ok: true, markets: mapSurveillanceMarkets(res, event) }
    }
    const res = await cmApi.getMarketsForSurveilliance(authHeaders(), body)
    assertSurveillanceListOk(res, SURVEILLANCE_STATIC_ERRORS.markets)
    return { ok: true, markets: mapSurveillanceMarkets(res, event) }
  } catch (err) {
    throw surveillanceCatchError(err, SURVEILLANCE_STATIC_ERRORS.markets)
  }
}

async function httpSearchMembers(filter: string): Promise<SearchMembersResult> {
  try {
    const rows = await searchHierarchyNew({ filter: filter.trim(), page: 1 })
    return { ok: true, usernames: searchHierarchyNewUsernames(rows) }
  } catch (err) {
    throw surveillanceCatchError(
      err,
      'Unable to search members. Please try again.',
    )
  }
}

async function httpGetGraph(filter: SurveillanceGraphFilter): Promise<GetGraphResult> {
  const stakeErr = validateStakeRange(filter.fromStake, filter.toStake)
  if (stakeErr) return { ok: false, message: stakeErr }
  if (!filter.eventType || !filter.eventId || !filter.marketId) {
    return { ok: false, message: 'Select sub game, event and market' }
  }

  const body: {
    eventType: string
    event: string
    market: string
    currencyType: string
    playerName: string[]
    fromStake?: number
    toStake?: number
    fromDate?: string
    toDate?: string
  } = {
    eventType: filter.eventType,
    event: filter.eventId,
    market: filter.marketId,
    currencyType: filter.currencyType,
    playerName: filter.playerNames,
  }
  if (filter.fromStake != null) body.fromStake = filter.fromStake
  if (filter.toStake != null) body.toStake = filter.toStake
  // Live + archive bet graph APIs filter matched_at when dates are set.
  if (filter.fromDate?.trim()) body.fromDate = filter.fromDate.trim()
  if (filter.toDate?.trim()) body.toDate = filter.toDate.trim()

  try {
    const res =
      filter.mode === 'archive'
        ? await cmApi.bqGetBetsForSurv(authHeaders(), body)
        : await cmApi.getBetsForSurveilliance(authHeaders(), body)
    return mapGetBetsForSurveilliance(res)
  } catch (err) {
    return {
      ok: false,
      message: surveillanceCatchError(err, SURVEILLANCE_STATIC_ERRORS.bets).message,
    }
  }
}

async function httpRegenerateGraph(
  marketId: string,
  graphType: SurveillanceGraphViewType = 'NORMAL',
  eventId?: string,
): Promise<RegenerateGraphResult> {
  const id = marketId.trim()
  if (!id) {
    return { ok: true, message: 'Unable to generate graph' }
  }
  const body: {
    marketId: string
    graphType: string
    eventId?: string
    wait: boolean
  } = {
    marketId: id,
    graphType,
    wait: true,
  }
  const event = (eventId ?? '').trim()
  if (event) body.eventId = event

  try {
    const res = await cmApi.uploadToBucketNew(authHeaders(), body)
    const msg =
      (typeof res.message === 'string' && res.message.trim()) ||
      (typeof res.data?.msg === 'string' && res.data.msg.trim()) ||
      'Graph generation started'
    return { ok: true, message: msg }
  } catch (err) {
    throw surveillanceCatchError(err, SURVEILLANCE_STATIC_ERRORS.regenerate)
  }
}

export type ListEventsInput = {
  mode: SurveillanceMode
  subGameId: string
  /** Sport name — Lab / CM-API `subGame` filter (e.g. "Cricket"). */
  subGameName?: string
  keyword?: string
  fromDate?: string
  toDate?: string
  playerNames?: string[]
  fromStake?: number | null
  toStake?: number | null
  currencyType?: string
}

export type ListMarketsInput = {
  /** When `archive`, calls BQ `bqGetMarketsForSurv`; otherwise live Postgres. */
  mode?: SurveillanceMode
  eventId: string
  fromDate?: string
  toDate?: string
  subGameName?: string
}

export const surveillanceClient = {
  listSubGames(
    mode: SurveillanceMode,
    opts?: ListSubGamesOpts,
  ): Promise<ListSubGamesResult> {
    return USE_STATIC_SURVEILLANCE
      ? staticListSubGames(mode)
      : httpListSubGames(mode, opts)
  },

  listEvents(input: ListEventsInput): Promise<ListEventsResult> {
    return USE_STATIC_SURVEILLANCE
      ? staticListEvents(input)
      : httpListEvents(input)
  },

  listMarkets(input: ListMarketsInput): Promise<ListMarketsResult> {
    return USE_STATIC_SURVEILLANCE
      ? staticListMarkets(input)
      : httpListMarkets(input)
  },

  searchMembers(filter: string): Promise<SearchMembersResult> {
    return USE_STATIC_SURVEILLANCE
      ? staticSearchMembers(filter)
      : httpSearchMembers(filter)
  },

  getGraph(filter: SurveillanceGraphFilter): Promise<GetGraphResult> {
    return USE_STATIC_SURVEILLANCE ? staticGetGraph(filter) : httpGetGraph(filter)
  },

  regenerateGraph(
    marketId: string,
    graphType: SurveillanceGraphViewType = 'NORMAL',
    eventId?: string,
  ): Promise<RegenerateGraphResult> {
    return USE_STATIC_SURVEILLANCE
      ? staticRegenerateGraph(marketId, graphType)
      : httpRegenerateGraph(marketId, graphType, eventId)
  },
}
